import React, { useEffect, useState } from 'react';
import {
  FilterBar,
  ExportCsvButton,
  EmptyState,
  AdminNotice,
  CloseButton,
  DataTable,
} from '../../../components/admin/Shared';
import {
  DateRangeDropdown,
  forwardPresets,
} from '../../../components/DateDropdown';
import { axiosKK } from '../../../services/networkRequest';
import {
  WarehouseForecastCell,
  WarehouseForecastResponse,
} from '../../../models/APIModels';
import errorMessage from '../../../services/errorMessage';

interface ForecastDetailOrder {
  orderId: number;
  customer?: string;
  postcode?: string;
  boxes?: number;
}
interface ForecastDetailResponse {
  date: string;
  type: 'inbound' | 'outbound';
  orders: ForecastDetailOrder[];
}

interface ForecastTabProps {
  partnerId: number | null;
}

export default function ForecastTab({ partnerId }: ForecastTabProps) {
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [cells, setCells] = useState<WarehouseForecastCell[]>([]);
  const [loading, setLoading] = useState(true);
  const [netError, setNetError] = useState<string>();

  const [drawer, setDrawer] = useState<ForecastDetailResponse | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  useEffect(() => {
    if (partnerId == null) {
      setCells([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNetError(undefined);
    const params: any = { partnerId };
    if (from) params.from = from;
    if (to) params.to = to;
    axiosKK
      .get<WarehouseForecastResponse>('/logistics/dashboard/warehouse/forecast', { params })
      .then(({ data }) => {
        if (!cancelled) setCells(data.cells || []);
      })
      .catch((err) => {
        if (!cancelled) errorMessage(setNetError)(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [from, to, partnerId]);

  const openDetails = (date: string, type: 'inbound' | 'outbound') => {
    if (partnerId == null) return;
    setDrawer({ date, type, orders: [] });
    setDrawerLoading(true);
    axiosKK
      .get<ForecastDetailResponse>(
        `/logistics/dashboard/warehouse/forecast/${date}/details`,
        { params: { type, partnerId } }
      )
      .then(({ data }) => setDrawer(data))
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setDrawerLoading(false));
  };

  return (
    <>
      <AdminNotice intent="error">{netError}</AdminNotice>

      <section className="admin-warehouse-forecast">
        <FilterBar
          actions={
            <ExportCsvButton
              endpoint="/logistics/dashboard/warehouse/forecast"
              filename={`warehouse-forecast-${from || 'all'}.csv`}
              params={{
                partnerId: partnerId ?? undefined,
                from: from || undefined,
                to: to || undefined,
              }}
              disabled={partnerId == null}
            />
          }
        >
          <FilterBar.Item label="Date range" htmlFor="forecast-date-range">
            <DateRangeDropdown
              id="forecast-date-range"
              value={{ start: from || null, end: to || null }}
              onChange={({ start, end }) => {
                setFrom(start ?? '');
                setTo(end ?? '');
              }}
              label="Forecast date range"
              placeholder="All upcoming"
              presets={forwardPresets()}
            />
          </FilterBar.Item>
        </FilterBar>

        {partnerId == null ? (
          <EmptyState
            variant="select-prerequisite"
            title="Select a partner"
            body="Pick a partner from the picker above to load the forecast."
          />
        ) : loading ? (
          <EmptyState title="Loading forecast…" />
        ) : cells.length === 0 ? (
          <EmptyState title="No forecast data." />
        ) : (
          // WHY bespoke: the matrix renders 30 working days as a fixed-column grid
          // with click-through cells; DataTable's row-card mobile mode wouldn't preserve
          // the per-day inbound/outbound layout that admins scan visually.
          <table className="pricing-table forecast-grid">
            <thead>
              <tr>
                <th>Date</th>
                <th>Day</th>
                <th>Inbound Orders</th>
                <th>Inbound Boxes</th>
                <th>Outbound Orders</th>
                <th>Outbound Boxes</th>
              </tr>
            </thead>
            <tbody>
              {cells.map((c) => (
                <tr key={c.date}>
                  <td>{c.displayDate}</td>
                  <td>{c.dayName}</td>
                  <td
                    className="forecast-grid__cell forecast-grid__cell--click"
                    onClick={() => openDetails(c.date, 'inbound')}
                  >
                    {c.inboundOrders}
                  </td>
                  <td>{c.inboundBoxes}</td>
                  <td
                    className="forecast-grid__cell forecast-grid__cell--click"
                    onClick={() => openDetails(c.date, 'outbound')}
                  >
                    {c.outboundOrders}
                  </td>
                  <td>{c.outboundBoxes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {drawer && (
        <div className="logistics-modal__overlay" onClick={() => setDrawer(null)}>
          <div className="logistics-modal__container" onClick={(e) => e.stopPropagation()}>
            <div className="logistics-modal__header">
              <div className="logistics-modal__header-info">
                <h3>
                  {drawer.type === 'inbound' ? 'Inbound' : 'Outbound'} · {drawer.date}
                </h3>
              </div>
              <CloseButton onClick={() => setDrawer(null)} ariaLabel="Close forecast details" />
            </div>
            <div className="logistics-modal__body">
              {drawerLoading ? (
                <EmptyState title="Loading…" />
              ) : (
                <DataTable<ForecastDetailOrder>
                  columns={[
                    { key: 'orderId', label: 'Order', render: (o) => `#${o.orderId}` },
                    { key: 'customer', label: 'Customer', render: (o) => o.customer || '—' },
                    { key: 'postcode', label: 'Postcode', render: (o) => o.postcode || '—' },
                    { key: 'boxes', label: 'Boxes', render: (o) => o.boxes ?? '—' },
                  ]}
                  rows={drawer.orders}
                  keyOf={(o) => o.orderId}
                  emptyMessage="No orders."
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
